// ============================================================
// VidyaTrack — Teacher Dashboard
// ============================================================

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiBook, FiCheckSquare, FiUsers, FiPlusCircle, FiBarChart2, FiGrid } from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";
import { format, isPast, isToday } from "date-fns";
import StatCard from "../../components/shared/StatCard";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

// Safe date converter (handles Firestore Timestamps)
const toDate = v => { if(!v) return new Date(); if(typeof v?.toDate==="function") return v.toDate(); return new Date(v); };





export default function TeacherDashboard() {
  const { userData } = useAuth();
  const { getHomework, getSubmissions, getStudents, getNotices, getGroups } = useData();
  const navigate = useNavigate();

  const [hw,      setHw]      = useState([]);
  const [subs,    setSubs]    = useState([]);
  const [students,setStudents]= useState([]);
  const [notices, setNotices] = useState([]);
  const [groups,  setGroups]  = useState([]);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const load = async () => {
      try {
        const [h, s, st, n, g] = await Promise.all([
          getHomework({ teacherId: userData?.uid }),
          getSubmissions({}),
          getStudents(),
          getNotices({}),
          getGroups({ teacherId: userData?.uid }),
        ]);
        setHw(h); setSubs(s); setStudents(st);
        setNotices(n.slice(0, 3)); setGroups(g);
      } catch { /* handled silently */ }
      finally { setLoading(false); }
    };
    if (userData?.uid) load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.uid]);

  const activeHw = hw.filter(h => !isPast(toDate(h.deadline)));

  const chartData = hw.slice(0, 6).map(h => ({
    name : h.title.slice(0, 10) + (h.title.length > 10 ? "…" : ""),
    submitted: h.submissionsCount || 0,
    total    : h.totalStudents    || 0,
    pct      : h.totalStudents > 0 ? Math.round(((h.submissionsCount||0) / h.totalStudents) * 100) : 0,
  }));

  const pendingReviews = subs.filter(s => s.status === "submitted").length;

  if (loading) return <div className="loading-center"><div className="spinner"/></div>;

  return (
    <div className="fade-in">
      <div className="page-head">
        <div>
          <h1 className="page-title">Teacher Dashboard 👩‍🏫</h1>
          <p className="page-sub">{format(new Date(), "EEEE, MMMM d, yyyy")}{userData?.subject ? ` · ${userData.subject}` : ""}</p>
        </div>
        <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
          <button className="btn btn-primary" onClick={() => navigate("/teacher/homework")}><FiPlusCircle size={15}/> Assign Homework</button>
          <button className="btn btn-secondary" onClick={() => navigate("/teacher/attendance")}><FiCheckSquare size={15}/> Mark Attendance</button>
        </div>
      </div>

      <div className="g g4" style={{ marginBottom:20 }}>
        <StatCard icon={<FiBook/>}        label="Active Homework"  value={activeHw.length}    color="var(--blue-700)" bg="var(--blue-100)"  onClick={() => navigate("/teacher/homework")}/>
        <StatCard icon={<FiUsers/>}       label="My Students"      value={students.length}    color="var(--green-600)"    bg="var(--green-50)"  onClick={() => navigate("/teacher/attendance")}/>
        <StatCard icon={<FiGrid/>}        label="Groups"           value={groups.length}      color="var(--amber-600)"   bg="var(--amber-50)"  onClick={() => navigate("/teacher/groups")}/>
        <StatCard icon={<FiBarChart2/>}   label="Pending Reviews"  value={pendingReviews}     color="#7c3aed"         bg="#f5f3ff"/>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 300px", gap:16, alignItems:"start" }}>
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

          {/* Submission chart */}
          {chartData.length > 0 && (
            <div className="card">
              <div className="card-header"><span className="card-title">📊 Homework Submission Rate</span></div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData} margin={{ top:5, right:10, bottom:36, left:-10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/>
                    <XAxis dataKey="name" tick={{ fontSize:11 }} angle={-25} textAnchor="end"/>
                    <YAxis tick={{ fontSize:11 }} domain={[0,100]}/>
                    <Tooltip contentStyle={{ borderRadius:8, fontSize:".82rem" }} formatter={v => [`${v}%`,"Completion"]}/>
                    <Bar dataKey="pct" fill="#054bb4" radius={[4,4,0,0]} name="pct"/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Active Homework table */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">📚 Active Homework</span>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate("/teacher/homework")}>Manage</button>
            </div>
            <div className="card-body" style={{ padding:0 }}>
              {activeHw.length === 0 ? (
                <div className="empty-state"><div className="empty-icon">📚</div><div className="empty-title">No active homework</div></div>
              ) : (
                <div className="tbl-wrap">
                  <table className="data-tbl">
                    <thead><tr><th>Title</th><th>Class</th><th>Deadline</th><th>Submissions</th></tr></thead>
                    <tbody>
                      {activeHw.map(h => {
                        const pct = h.totalStudents > 0 ? Math.round(((h.submissionsCount||0)/h.totalStudents)*100) : 0;
                        return (
                          <tr key={h.id}>
                            <td>
                              <div style={{ fontWeight:600, fontSize:".88rem" }}>{h.title}</div>
                              <div style={{ fontSize:".72rem", color:"var(--gray-500)" }}>{h.subject}</div>
                            </td>
                            <td style={{ fontSize:".84rem" }}>{h.className}</td>
                            <td style={{ fontSize:".82rem", color:isToday(toDate(h.deadline))?"var(--red-600)":"var(--gray-700)" }}>
                              {format(toDate(h.deadline),"MMM d")}
                            </td>
                            <td>
                              <div style={{ fontSize:".82rem", marginBottom:3 }}>{h.submissionsCount||0}/{h.totalStudents||0}</div>
                              <div className="progress"><div className={`progress-bar ${pct>=70?"green":pct>=40?"amber":"red"}`} style={{ width:`${pct}%` }}/></div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right */}
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">Quick Actions</span></div>
            <div className="card-body" style={{ display:"flex", flexDirection:"column", gap:7 }}>
              {[
                { icon:"📚", label:"Assign Homework",   path:"/teacher/homework" },
                { icon:"✅", label:"Mark Attendance",   path:"/teacher/attendance" },
                { icon:"📤", label:"Upload Materials",  path:"/teacher/materials" },
                { icon:"👥", label:"Create Group",      path:"/teacher/groups" },
                { icon:"📢", label:"Post Notice",       path:"/teacher/notices" },
                { icon:"📊", label:"View Reports",      path:"/teacher/reports" },
              ].map(a => (
                <button key={a.path} onClick={() => navigate(a.path)} style={{
                  display:"flex", alignItems:"center", gap:10,
                  padding:"10px 12px", background:"var(--gray-100)",
                  border:"1px solid var(--border)", borderRadius:"var(--r8)",
                  cursor:"pointer", fontFamily:"inherit", transition:"var(--t)",
                  color:"var(--gray-700)", fontSize:".86rem", fontWeight:500,
                }}
                onMouseEnter={e => { e.currentTarget.style.background="var(--blue-100)"; e.currentTarget.style.borderColor="var(--blue-700)"; e.currentTarget.style.color="var(--blue-700)"; }}
                onMouseLeave={e => { e.currentTarget.style.background="var(--gray-100)"; e.currentTarget.style.borderColor="var(--border)"; e.currentTarget.style.color="var(--gray-700)"; }}
                >
                  <span style={{ fontSize:"1.05rem" }}>{a.icon}</span>{a.label}
                </button>
              ))}
            </div>
          </div>

          {notices.length > 0 && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">🔔 Recent Notices</span>
                <button className="btn btn-secondary btn-sm" onClick={() => navigate("/teacher/notices")}>All</button>
              </div>
              <div className="card-body" style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {notices.map(n => (
                  <div key={n.id} style={{ padding:"10px 12px", background:n.priority==="high"?"var(--red-50)":"var(--blue-100)", borderRadius:"var(--r6)", borderLeft:`3px solid ${n.priority==="high"?"var(--red-600)":"var(--blue-700)"}` }}>
                    <div style={{ fontSize:".84rem", fontWeight:600 }}>{n.title}</div>
                    <div style={{ fontSize:".73rem", color:"var(--gray-500)", marginTop:2 }}>{n.content?.slice(0,60)}…</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`@media(max-width:1024px){div[style*="grid-template-columns:1fr 300px"]{grid-template-columns:1fr!important}}`}</style>

    </div>
  );
}